// Tableau des cheminements : paramètres éditables (type, nombre de câbles, réserve, chutes, verticales) et longueurs calculées
// par le serveur. Réserve et chutes restent des colonnes séparées ; une longueur non fiable est signalée comme telle.
import { useEffect, useState } from 'react';
import { CheckCircle2, Route, Trash2 } from 'lucide-react';
import { Badge, Bouton, EtatVide } from '../../components/ui.tsx';
import { dateHeure, nombre } from '../../lib/format.ts';
import { TYPES_CABLE_SUGGERES, lireNombre, type CheminementVue } from './modele.ts';

type Champ = 'nom' | 'typeCable' | 'nbCables' | 'reservePct' | 'chutesPct' | 'verticalesM';

function Cellule({ valeur, libelle, desactive, nombreDe, liste, valider, enregistrer, mono }: {
  valeur: string; libelle: string; desactive: boolean; nombreDe?: { min: number; max: number; entier?: boolean }; liste?: string;
  valider?: (v: string) => boolean; enregistrer: (v: string) => void; mono?: boolean;
}) {
  const [v, setV] = useState(valeur);
  useEffect(() => { setV(valeur); }, [valeur]);
  const lu = nombreDe ? lireNombre(v) : null;
  const invalide = nombreDe
    ? lu === null || lu < nombreDe.min || lu > nombreDe.max || (!!nombreDe.entier && !Number.isInteger(lu))
    : valider ? !valider(v) : false;
  const appliquer = () => { if (!invalide && v.trim() !== valeur) enregistrer(v.trim()); };
  return (
    <input className={`saisie ${mono ? 'saisie-mono' : ''} ${nombreDe ? 'cch-saisie-nombre' : ''}`} value={v} disabled={desactive} aria-label={libelle}
      aria-invalid={invalide || undefined} inputMode={nombreDe ? 'decimal' : undefined} list={liste}
      onChange={(e) => setV(e.target.value)} onBlur={appliquer} onKeyDown={(e) => { if (e.key === 'Enter') appliquer(); if (e.key === 'Escape') setV(valeur); }} />
  );
}

function Metre({ v, fiable, mention }: { v: string | null; fiable: boolean; mention: string | null }) {
  if (v === null) return <span className="inconnu" title={mention ?? undefined}>Non calculée</span>;
  return (
    <span className={fiable ? 'chiffre' : 'chiffre cch-non-fiable'} title={fiable ? undefined : mention ?? undefined}>
      {nombre(v, 2)} m{!fiable && <span className="visuellement-cache"> (non fiable)</span>}
    </span>
  );
}

export function TableauCheminements({ cheminements, route, choisir, ecriture, peutValider, maj, valider, supprimer }: {
  cheminements: CheminementVue[]; route: string | null; choisir: (id: string) => void; ecriture: boolean; peutValider: boolean;
  maj: (id: string, champ: Champ, valeur: string) => void; valider: (id: string, ok: boolean) => void; supprimer: (c: CheminementVue) => void;
}) {
  if (!cheminements.length) {
    return <EtatVide icone={<Route />} titre="Aucun cheminement sur ce plan"
      texte={ecriture ? 'Choisissez « Tracer un cheminement », puis cliquez sur le plan point par point le long du passage des câbles.' : 'Aucun passage de câble n’a été tracé.'} />;
  }
  return (
    <div className="tableau-conteneur">
      <datalist id="cch-types-cable">{TYPES_CABLE_SUGGERES.map((t) => <option key={t} value={t} />)}</datalist>
      <table className="tableau cch-tableau cch-tableau-chem">
        <thead>
          <tr>
            <th>Cheminement</th><th>Câble / liaison</th><th className="num">Câbles / paire</th><th className="num">Réserve %</th><th className="num">Chutes %</th>
            <th className="num">Verticales</th><th className="num">Plane / câble</th><th className="num">Par câble</th><th className="num">Total posé</th>
            <th className="num">Réserve</th><th className="num">Chutes</th><th className="num">À commander</th><th>Validation technique</th>
            <th><span className="visuellement-cache">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {cheminements.map((c) => {
            const l = c.longueurs;
            const des = !ecriture;
            return (
              <tr key={c.id} className={route === c.id ? 'est-choisi' : undefined} onFocusCapture={() => choisir(c.id)}>
                <td data-libelle="Cheminement"><Cellule valeur={c.nom} libelle="Nom du cheminement" desactive={des} valider={(v) => !!v.trim() && v.length <= 120}
                  enregistrer={(v) => maj(c.id, 'nom', v)} /></td>
                <td data-libelle="Câble / liaison" className="cch-large">{c.nature === 'frigorifique' ? <span>Liaison frigorifique<br />Liquide : {c.refrigeration?.liquide?.mm ?? 'à saisir'} mm<br />Gaz : {c.refrigeration?.gaz?.mm ?? 'à saisir'} mm</span>
                  : <Cellule valeur={c.typeCable ?? ''} libelle={`Type de câble de « ${c.nom} »`} desactive={des} liste="cch-types-cable"
                  valider={(v) => v.length <= 120} enregistrer={(v) => maj(c.id, 'typeCable', v)} />}</td>
                <td data-libelle="Câbles / paire" className="num">{c.nature === 'frigorifique' ? 'Paire liquide / gaz' : <Cellule valeur={String(c.nbCables)} libelle={`Nombre de câbles de « ${c.nom} »`} desactive={des}
                  nombreDe={{ min: 1, max: 500, entier: true }} enregistrer={(v) => maj(c.id, 'nbCables', v)} />}</td>
                <td data-libelle="Réserve %" className="num"><Cellule valeur={nombre(c.reservePct, 2)} libelle={`Réserve en % de « ${c.nom} »`} desactive={des}
                  nombreDe={{ min: 0, max: 100 }} enregistrer={(v) => maj(c.id, 'reservePct', v)} /></td>
                <td data-libelle="Chutes %" className="num"><Cellule valeur={nombre(c.chutesPct, 2)} libelle={`Chutes en % de « ${c.nom} »`} desactive={des}
                  nombreDe={{ min: 0, max: 100 }} enregistrer={(v) => maj(c.id, 'chutesPct', v)} /></td>
                <td data-libelle="Verticales (m)" className="num">{c.verticale || c.nature === 'frigorifique' ? <><Metre v={c.verticalesM} fiable={l.fiable} mention={c.sourceVerticale} /><p className="tres-petit secondaire">{c.sourceVerticale}</p></>
                  : <Cellule valeur={nombre(c.verticalesM, 2)} libelle={`Longueur verticale par câble (m) de « ${c.nom} »`} desactive={des}
                  nombreDe={{ min: 0, max: 10000 }} enregistrer={(v) => maj(c.id, 'verticalesM', v)} />}</td>
                <td data-libelle="Plane / câble" className="num"><Metre v={l.planeM} fiable={l.fiable} mention={l.mention} /></td>
                <td data-libelle="Par câble" className="num"><Metre v={l.parCableM} fiable={l.fiable} mention={l.mention} /></td>
                <td data-libelle="Total posé" className="num"><Metre v={l.totalM} fiable={l.fiable} mention={l.mention} /></td>
                <td data-libelle="Réserve" className="num"><Metre v={l.reserveM} fiable={l.fiable} mention={l.mention} /></td>
                <td data-libelle="Chutes" className="num"><Metre v={l.chutesM} fiable={l.fiable} mention={l.mention} /></td>
                <td data-libelle="À commander" className="num"><strong><Metre v={l.aCommanderM} fiable={l.fiable} mention={l.mention} /></strong></td>
                <td data-libelle="Validation">
                  {c.validation
                    ? <span className="pile" style={{ gap: 2 }}><Badge ton="succes">Validé</Badge><span className="tres-petit discret">{c.validation.par} · {dateHeure(c.validation.le)}</span></span>
                    : <Badge ton="ambre">À valider</Badge>}
                  {peutValider && (
                    <Bouton compact variante="fantome" icone={c.validation ? undefined : <CheckCircle2 aria-hidden />} onClick={() => valider(c.id, !c.validation)}>
                      {c.validation ? 'Retirer la validation' : 'Valider'}
                    </Bouton>
                  )}
                </td>
                <td className="cch-actions">
                  <Bouton compact variante="fantome" onClick={() => choisir(c.id)} aria-label={`Voir « ${c.nom} » sur le plan`}>Voir</Bouton>
                  {ecriture && <Bouton compact variante="fantome" className="btn-icone" icone={<Trash2 aria-hidden />} onClick={() => supprimer(c)} aria-label={`Supprimer « ${c.nom} »`} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
